import React, { useEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import SticeArcWall from '../../../3ds/bin/stice/SticeArcWall';
import {
    buildPreviewRoundBinOutlines,
    buildPreviewSticeBaseOutlines,
} from '../../../3ds/bin/stice/layoutPreviewShape';
import * as global from '../../../data/global';
import { CustomText3D } from "../../_shared/CustomText3D";
import CableNew from '../../../3ds/bin/cable/CableNew';
import FanBin3d from '../../../3ds/bin/fan/fan3d';
import {
    layoutWorldZForSite,
    siteUsesEditorLayoutAxes,
} from '../../../components/layoutPreviewView';
import {
    flatCableRadiansToNorth,
    flatBinProductionRotationCorrectionRad,
    flatFootprintRotationBakedInGeometry,
    flatBinUsesCatalogCableScale,
    orientedLayoutFootprintFt,
    btsCableRadiansFromDeg,
    flatProductionGroupRotationY,
    flatProductionCableRotationRad,
} from '../../../components/siteLayoutStice.js';
import { layoutCablesForLayoutBin } from '../../../components/layoutTableProxy.js';

const collectSticeBinMeshes = (binGroup) => {
    const meshes = [];
    if (!binGroup?.traverse) return meshes;
    binGroup.traverse((child) => {
        if (child.isMesh) meshes.push(child);
    });
    return meshes;
};

/** Editor / Preview Site: +z = canvas down. Production Site 3D: -z. */
const layoutWorldZ = layoutWorldZForSite;

const siteUsesEditorLayoutRotation = (site) =>
    !!(
        site?.isLayoutPreview ||
        site?.isLayoutPreviewSite ||
        site?.isLayoutPreview3d ||
        siteUsesEditorLayoutAxes(site) ||
        site?.layoutSavedFromSiteLayoutEditor
    );

const layoutBinHasBakedFlatOutline = (layoutBin) =>
    !layoutBin?.isRound &&
    (layoutBin.previewPolygonFt?.length >= 3 || layoutBin.loopClosed);

/** Rotate BTS cableList offsets to match flat orientation on canvas. */
const cableRadiansToNorth = (site, layoutBin) => {
    if (layoutBin && !layoutBin.isRound) {
        if (orientedLayoutFootprintFt(layoutBin, site)) {
            return null;
        }
        if (flatBinUsesCatalogCableScale(layoutBin, site)) {
            return null;
        }
        if (siteUsesEditorLayoutRotation(site)) {
            return flatCableRadiansToNorth(layoutBin, site);
        }
        if (flatFootprintRotationBakedInGeometry(layoutBin, site)) {
            return flatCableRadiansToNorth(layoutBin, site);
        }
        const groupSpin = flatProductionCableRotationRad(layoutBin, site);
        if (groupSpin != null) return groupSpin;
    }
    // Editor round bins are not spun on canvas; BTS topPointX/Z are already bin-local.
    if (layoutBin?.isRound && siteUsesEditorLayoutRotation(site)) {
        return null;
    }
    const catalogDeg =
        layoutBin?.bin?.degreesToNorth ??
        (site?.bins || []).find(
            (b) =>
                b.binDataId === layoutBin?.binId ||
                b.binId === layoutBin?.binId
        )?.degreesToNorth;
    const base = btsCableRadiansFromDeg(catalogDeg);
    const correction = flatBinProductionRotationCorrectionRad(layoutBin, site);
    if (base == null) return correction || null;
    return base + correction;
};

const cableRadiansInsideFlatProductionGroup = (site, layoutBin) => {
    if (orientedLayoutFootprintFt(layoutBin, site)) {
        return null;
    }
    if (flatBinUsesCatalogCableScale(layoutBin, site)) {
        return null;
    }
    if (siteUsesEditorLayoutRotation(site)) {
        return flatCableRadiansToNorth(layoutBin, site);
    }
    return null;
};

const previewLineColor = () =>
    global.gw.theme === "light" ? 0x000000 : 0xffff00;

/** Round-bin base rings in XZ (smooth line loops, same approach as flat stices). */
const LayoutPreviewRoundBase = ({ layoutBin }) => {
    const lines = useMemo(
        () => buildPreviewRoundBinOutlines(layoutBin.width),
        [layoutBin.width]
    );
    if (!lines.outer) return null;
    const color = previewLineColor();
    return (
        <>
            <lineLoop geometry={lines.outer}>
                <lineBasicMaterial color={color} />
            </lineLoop>
            {lines.inner ? (
                <lineLoop geometry={lines.inner}>
                    <lineBasicMaterial color={color} transparent opacity={0.65} />
                </lineLoop>
            ) : null}
        </>
    );
};

/** Flat stice base outline in XZ (no extrude / no fill — matches Site3d from above). */
const LayoutPreviewSticeBase = ({ layoutBin }) => {
    const lines = useMemo(
        () => buildPreviewSticeBaseOutlines(layoutBin?.previewPolygonFt),
        [layoutBin?.previewPolygonFt]
    );
    if (!lines.outer) return null;
    const color = previewLineColor();
    return (
        <>
            <lineLoop geometry={lines.outer}>
                <lineBasicMaterial color={color} />
            </lineLoop>
            {lines.inner ? (
                <lineLoop geometry={lines.inner}>
                    <lineBasicMaterial color={color} transparent opacity={0.65} />
                </lineLoop>
            ) : null}
        </>
    );
};

export const Stice = (props) => {
    const worldZ = (z) => layoutWorldZ(props.site, z);

    useEffect(() => {
        // Editor layout: walls/outline come from saved sides (rotation already baked in).
        if (siteUsesEditorLayoutRotation(props.site)) {
            return;
        }

        //  Adjust for flat houses
        const flatHouseRefs = [];
        //const roundHouseRefs = [];
        // First collect all flat house wall references
        for (let i = 0; i < props.site.bins.length; i++) {
            const bin = props.site.bins[i];
            if (bin && 
                props.site.groupsRef.length > 0 && 
                props.site.groupsRef[0].children.length > 0) {
                for (let j = 0; j < props.site.groupsRef[0].children.length; j++) {
                    const group = props.site.groupsRef[0].children[j];
                    if (group &&
                        group.children.length > 0 &&
                        group.layoutBin.binId === bin.binDataId) {
                            
                        //  This is the flat house wall
                        let bin3dModel = group.children[1];
                        if (bin3dModel) {
                            if (bin.type.toLowerCase().includes("flat")) {
                                const layoutBin = group.layoutBin;
                                // Match flatProductionGroupRotationY (0 for long-on-X / baked /
                                // editor layouts). Imperative set must not fight the JSX rotation.
                                const radiansToNorth = flatProductionGroupRotationY(
                                    layoutBin,
                                    props.site
                                );
                                bin3dModel.rotation.set(
                                    bin3dModel.rotation.x,
                                    radiansToNorth,
                                    bin3dModel.rotation.z
                                );
                                flatHouseRefs.push(group);
                            }
                            else {
                                //roundHouseRefs.push(group);
                            }
                        }
                    }
                }
            }
        }

        if (flatHouseRefs.length > 0 && flatHouseRefs.length !== props.site.bins.length) {
            for (let i = 0; i < props.site.bins.length; i++) {
                const bin = props.site.bins[i];
                if (bin && 
                    !bin.type.toLowerCase().includes("flat")) {
    
                    for (let j = 0; j < props.site.groupsRef[0].children.length; j++) {
                        const group = props.site.groupsRef[0].children[j];
                        if (group &&
                            group.children.length > 0 &&
                            group.layoutBin.binId === bin.binDataId) {
                                flatHouseRefs.push(group);
                        }
                    }
                }
            }
        }
    });

    const handleSelected = (e) => {
        e.stopPropagation();
        global.gw.selectedSite.orbitControls.enableDamping = false;
        if (props.site.mouseClickDownTime > 400) {
            global.gw.selectedSite.orbitControls.enableDamping = true;
            return;
        }
        const target = e.eventObject;
        let layoutBinId = target.binId ?? null;
        if (layoutBinId == null) {
            let node = target.parent;
            while (node) {
                if (node.binId != null) {
                    layoutBinId = node.binId;
                    break;
                }
                node = node.parent;
            }
        }
        if (layoutBinId != null) {
            global.highlightStice(layoutBinId);
        } else if (target.meshes?.length > 0) {
            for (const mesh of target.meshes) {
                if (mesh.binId && mesh.isWallMesh) {
                    global.highlightStice(mesh.binId);
                    break;
                }
            }
        }
        global.scaleBinText();
        global.gw.selectedSite.orbitControls.enableDamping = true;
    };

    return (
        <group
            ref={el => {
                if (!el) return;
                props.site.siteRef.push(el);
                props.site.offset = { x: el.position.x, y: el.position.y, z: el.position.z };
                if (!el.parent) return;
                props.site.sceneRef = el.parent;
            }}
        >
            {props.site.layoutGroups.map((layoutGroup) => (
                <group
                    key={`siteGroup_map_group_${layoutGroup.customerSiteLayoutGroupId}`}
                    position={[layoutGroup.x, layoutGroup.y, worldZ(layoutGroup.z)]}
                    ref={el => {
                        if (!el) return;
                        if (!props.site.groupsRef.includes(el)) {
                            el.name = layoutGroup.groupName;
                            el.groupId = layoutGroup.customerSiteLayoutGroupId;
                            el.isSelected = false;
                            el.width = layoutGroup.width;
                            el.length = layoutGroup.length;
                            el.height = layoutGroup.height;
                            el.binsRef = props.site.binsRef.filter(x => x.groupId === layoutGroup.customerSiteLayoutGroupId);
                            props.site.groupsRef.push(el);
                        }
                    }}
                >
                    {props.site.layoutBins.filter(x => x.customerSiteLayoutGroupId === layoutGroup.customerSiteLayoutGroupId).map((layoutBin) => (
                        layoutBin.isRound ?
                            <group
                                key={`roundBin_map_group_${layoutBin.customerSiteLayoutBinId}`}
                                position={[layoutBin.x, layoutBin.y, worldZ(layoutBin.z)]}
                                layoutBin={layoutBin}
                                ref={el => {
                                    if (!el) return;
                                    if (!props.site.binsRef.includes(el)) {
                                        el.name = layoutBin.binName;
                                        el.binName = layoutBin.binName;
                                        el.layoutBin = layoutBin;
                                        el.groupId = layoutBin.customerSiteLayoutGroupId;
                                        el.binId = layoutBin.customerSiteLayoutBinId;
                                        el.binDataId = layoutBin.binId;
                                        el.isRound = layoutBin.isRound;
                                        el.width = layoutBin.width;
                                        el.length = layoutBin.length;
                                        el.height = layoutBin.height;
                                        el.isSelected = false;
                                        props.site.binsRef.push(el);
                                    }
                                    el.meshes = collectSticeBinMeshes(el);
                                    // Update bin name color to match cable name color in light mode
                                    if (el.children && el.children[0] && el.children[0].children && el.children[0].children[0] && el.children[0].children[0].children && el.children[0].children[0].children[0] && el.children[0].children[0].children[0].children && el.children[0].children[0].children[0].children[0] && el.children[0].children[0].children[0].children[0].material) {
                                        let binTextMaterial = el.children[0].children[0].children[0].children[0].material;
                                        const binTextColor = global.gw.theme === 'light' ? "#878787" : "#898989";
                                        binTextMaterial.color.set(binTextColor);
                                        binTextMaterial.emissive.set(binTextColor);
                                        binTextMaterial.transparent = false;
                                        binTextMaterial.opacity = 1;
                                        binTextMaterial.needsUpdate = true;
                                    }
                                }}
                                onClick={handleSelected}
                            >
                                <group position={[0, -2.25, 0]}>
                                    <CustomText3D
                                        scale={global.gw.selectedSite.binTextScale}
                                        side={THREE.DoubleSide}
                                        position={[0, 0, 0]}
                                        meshProps={{
                                            material: new THREE.MeshStandardMaterial({
                                                color: global.gw.theme === 'light' ? 0x878787 : 0xCCCCCC,
                                                side: THREE.FrontSide,
                                                toneMapped: false,
                                                envMapIntensity: 1,
                                                metalness: 0.0,
                                                transparent: false,
                                                opacity: 1,
                                            }),
                                        }}
                                    >
                                        {layoutBin.binName}
                                    </CustomText3D>
                                    {(() => {
                                        const binId = layoutBin.binId;
                                        const binRef = global.gw.selectedSite?.binsRef?.find(b => b.binDataId === binId);
                                        const lookupByDataId = binId != null ? (typeof binId === 'number' ? binId : parseInt(binId, 10)) : null;
                                        const lookupBySeqId = binRef?.binId != null ? (typeof binRef.binId === 'number' ? binRef.binId : parseInt(binRef.binId, 10)) : null;
                                        const binGradeData = (lookupByDataId != null && !isNaN(lookupByDataId) && global.gw.selectedSite?.binGrades?.has(lookupByDataId)) ? global.gw.selectedSite.binGrades.get(lookupByDataId)
                                            : (lookupBySeqId != null && !isNaN(lookupBySeqId) && global.gw.selectedSite?.binGrades?.has(lookupBySeqId)) ? global.gw.selectedSite.binGrades.get(lookupBySeqId) : null;
                                        let binDateRead = binGradeData?.dateRead ?? null;
                                        if (!binDateRead) {
                                            const binLayoutCables = layoutCablesForLayoutBin(props.site, layoutBin);
                                            if (binLayoutCables.length > 0 && global.gw.selectedSite?.cableGrades) {
                                                const dates = binLayoutCables.map(lc => {
                                                    const key = `${layoutBin.binId}_${lc.cableId}`;
                                                    const cg = global.gw.selectedSite.cableGrades.get(key);
                                                    return cg?.dateRead;
                                                }).filter(Boolean);
                                                binDateRead = dates.length > 0 ? dates.reduce((a, b) => new Date(a) > new Date(b) ? a : b) : null;
                                            }
                                        }
                                        const grade = binGradeData?.grade ?? global.gw.selectedSite?.siteGrade ?? null;
                                        const avgTime = binGradeData?.averageTimeToRead ?? null;
                                        const dateStr = binDateRead ? global.jsonDateToFormatDateOnly(binDateRead) : '';
                                        const showGradesOrDate = global.gw.selectedSite?.isGradesOn !== false;
                                        return (
                                            <>
                                                {showGradesOrDate && dateStr ? (
                                                    <CustomText3D
                                                        scale={global.gw.selectedSite.binTextScale}
                                                        side={THREE.DoubleSide}
                                                        position={[0, -4.0, 0]}
                                                        meshProps={{
                                                            material: new THREE.MeshStandardMaterial({
                                                                color: global.gw.theme === 'light' ? 0xb4b4b4 : 0xCCCCCC,
                                                                side: THREE.FrontSide,
                                                                toneMapped: false,
                                                                envMapIntensity: 1,
                                                                metalness: 0.0,
                                                                transparent: false,
                                                                opacity: 1,
                                                            }),
                                                        }}
                                                    >
                                                        {dateStr}
                                                    </CustomText3D>
                                                ) : null}
                                                {!global.gw.selectedSite?.isHms && grade != null && showGradesOrDate ? (
                                                    <CustomText3D
                                                        scale={global.gw.selectedSite.binTextScale}
                                                        side={THREE.DoubleSide}
                                                        position={[0, (showGradesOrDate && dateStr) ? -8.0 : -4.0, 0]}
                                                        meshProps={{
                                                            material: new THREE.MeshStandardMaterial({
                                                                color: global.gw.theme === 'light' ? 0xb4b4b4 : 0xCCCCCC,
                                                                side: THREE.FrontSide,
                                                                toneMapped: false,
                                                                envMapIntensity: 1,
                                                                metalness: 0.0,
                                                                transparent: false,
                                                                opacity: 1,
                                                            }),
                                                        }}
                                                    >
                                                        {`GQCS ${Math.round(grade)}%`}{avgTime != null && !isNaN(avgTime) ? ` ${avgTime}s` : ''}
                                                    </CustomText3D>
                                                ) : null}
                                            </>
                                        );
                                    })()}
                                </group>
                                {props.site.isLayoutPreview ? (
                                    <LayoutPreviewRoundBase layoutBin={layoutBin} />
                                ) : (
                                    <group
                                        key={`roundWall_map_group_${layoutBin.customerSiteLayoutSideId}`}
                                        position={[0, 1.25 / 2.0 - 2, 0]}
                                    >
                                        <SticeArcWall
                                            radiusTop={layoutBin.width / 2.0}
                                            radiusBottom={layoutBin.width / 2.0}
                                            height={1.25}
                                            radialSegments={64}
                                            heightSegments={20}
                                            openEnded={true}
                                            binId={layoutBin.customerSiteLayoutBinId}
                                            binName={layoutBin.binName}
                                            groupId={layoutBin.customerSiteLayoutGroupId}
                                        />
                                    </group>
                                )}
                                {layoutCablesForLayoutBin(props.site, layoutBin).map(layoutCable => (
                                    <CableNew
                                        key={`roundBin_cable_${layoutCable.customerSiteLayoutCableId}`}
                                        layoutCable={layoutCable}
                                        layoutSensors={props.site.layoutSensors.filter(x => x.customerSiteLayoutCableId === layoutCable.customerSiteLayoutCableId)}
                                        layoutBin={layoutBin}
                                        site={props.site}
                                        radiansToNorth={cableRadiansToNorth(props.site, layoutBin)}
                                    />
                                ))}
                                <FanBin3d
                                    layoutBin={layoutBin}
                                    site={props.site}
                                />
                            </group>
                            :
                            <group
                                key={`sticeBin_map_group_${layoutBin.customerSiteLayoutBinId}`}
                                position={[layoutBin.x, layoutBin.y, worldZ(layoutBin.z)]}
                                layoutBin={layoutBin}
                                ref={el => {
                                    if (!el) return;
                                    if (!props.site.binsRef.includes(el)) {
                                        el.name = layoutBin.binName;
                                        el.binName = layoutBin.binName;
                                        el.layoutBin = layoutBin;
                                        el.groupId = layoutBin.customerSiteLayoutGroupId;
                                        el.binId = layoutBin.customerSiteLayoutBinId;
                                        el.binDataId = layoutBin.binId;
                                        el.isRound = layoutBin.isRound;
                                        el.width = layoutBin.width;
                                        el.length = layoutBin.length;
                                        el.height = layoutBin.height;
                                        el.isSelected = false;
                                        props.site.binsRef.push(el);
                                    }
                                    el.meshes = collectSticeBinMeshes(el);
                                    // Update bin name color to match cable name color in light mode
                                    if (el.children && el.children[0] && el.children[0].children && el.children[0].children[0] && el.children[0].children[0].children && el.children[0].children[0].children[0] && el.children[0].children[0].children[0].children && el.children[0].children[0].children[0].children[0] && el.children[0].children[0].children[0].children[0].material) {
                                        let binTextMaterial = el.children[0].children[0].children[0].children[0].material;
                                        const binTextColor = global.gw.theme === 'light' ? "#878787" : "#898989";
                                        binTextMaterial.color.set(binTextColor);
                                        binTextMaterial.emissive.set(binTextColor);
                                        binTextMaterial.transparent = false;
                                        binTextMaterial.opacity = 1;
                                        binTextMaterial.needsUpdate = true;
                                    }
                                }}
                                onClick={handleSelected}
                            >
                                <group position={[0, -2.25, 0]}>
                                    <CustomText3D
                                        scale={global.gw.selectedSite.binTextScale}
                                        side={THREE.DoubleSide}
                                        position={[0, 0, 0]}
                                        meshProps={{
                                            material: new THREE.MeshStandardMaterial({
                                                color: global.gw.theme === 'light' ? 0x878787 : 0xCCCCCC,
                                                side: THREE.FrontSide,
                                                toneMapped: false,
                                                envMapIntensity: 1,
                                                metalness: 0.0,
                                                transparent: false,
                                                opacity: 1,
                                            }),
                                        }}
                                    >
                                        {layoutBin.binName}
                                    </CustomText3D>
                                    {(() => {
                                        const binId = layoutBin.binId;
                                        const binRef = global.gw.selectedSite?.binsRef?.find(b => b.binDataId === binId);
                                        const lookupByDataId = binId != null ? (typeof binId === 'number' ? binId : parseInt(binId, 10)) : null;
                                        const lookupBySeqId = binRef?.binId != null ? (typeof binRef.binId === 'number' ? binRef.binId : parseInt(binRef.binId, 10)) : null;
                                        const binGradeData = (lookupByDataId != null && !isNaN(lookupByDataId) && global.gw.selectedSite?.binGrades?.has(lookupByDataId)) ? global.gw.selectedSite.binGrades.get(lookupByDataId)
                                            : (lookupBySeqId != null && !isNaN(lookupBySeqId) && global.gw.selectedSite?.binGrades?.has(lookupBySeqId)) ? global.gw.selectedSite.binGrades.get(lookupBySeqId) : null;
                                        let binDateRead = binGradeData?.dateRead ?? null;
                                        if (!binDateRead) {
                                            const binLayoutCables = layoutCablesForLayoutBin(props.site, layoutBin);
                                            if (binLayoutCables.length > 0 && global.gw.selectedSite?.cableGrades) {
                                                const dates = binLayoutCables.map(lc => {
                                                    const key = `${layoutBin.binId}_${lc.cableId}`;
                                                    const cg = global.gw.selectedSite.cableGrades.get(key);
                                                    return cg?.dateRead;
                                                }).filter(Boolean);
                                                binDateRead = dates.length > 0 ? dates.reduce((a, b) => new Date(a) > new Date(b) ? a : b) : null;
                                            }
                                        }
                                        const grade = binGradeData?.grade ?? global.gw.selectedSite?.siteGrade ?? null;
                                        const avgTime = binGradeData?.averageTimeToRead ?? null;
                                        const dateStr = binDateRead ? global.jsonDateToFormatDateOnly(binDateRead) : '';
                                        const showGradesOrDate = global.gw.selectedSite?.isGradesOn !== false;
                                        return (
                                            <>
                                                {showGradesOrDate && dateStr ? (
                                                    <CustomText3D
                                                        scale={global.gw.selectedSite.binTextScale}
                                                        side={THREE.DoubleSide}
                                                        position={[0, -4.0, 0]}
                                                        meshProps={{
                                                            material: new THREE.MeshStandardMaterial({
                                                                color: global.gw.theme === 'light' ? 0xb4b4b4 : 0xCCCCCC,
                                                                side: THREE.FrontSide,
                                                                toneMapped: false,
                                                                envMapIntensity: 1,
                                                                metalness: 0.0,
                                                                transparent: false,
                                                                opacity: 1,
                                                            }),
                                                        }}
                                                    >
                                                        {dateStr}
                                                    </CustomText3D>
                                                ) : null}
                                                {!global.gw.selectedSite?.isHms && grade != null && showGradesOrDate ? (
                                                    <CustomText3D
                                                        scale={global.gw.selectedSite.binTextScale}
                                                        side={THREE.DoubleSide}
                                                        position={[0, (showGradesOrDate && dateStr) ? -8.0 : -4.0, 0]}
                                                        meshProps={{
                                                            material: new THREE.MeshStandardMaterial({
                                                                color: global.gw.theme === 'light' ? 0xb4b4b4 : 0xCCCCCC,
                                                                side: THREE.FrontSide,
                                                                toneMapped: false,
                                                                envMapIntensity: 1,
                                                                metalness: 0.0,
                                                                transparent: false,
                                                                opacity: 1,
                                                            }),
                                                        }}
                                                    >
                                                        {`GQCS ${Math.round(grade)}%`}{avgTime != null && !isNaN(avgTime) ? ` ${avgTime}s` : ''}
                                                    </CustomText3D>
                                                ) : null}
                                            </>
                                        );
                                    })()}
                                </group>
                                <group
                                    rotation={[
                                        0,
                                        flatProductionGroupRotationY(
                                            layoutBin,
                                            props.site
                                        ),
                                        0,
                                    ]}
                                >
                                {props.site.isLayoutPreview ? (
                                    <LayoutPreviewSticeBase layoutBin={layoutBin} />
                                ) : (
                                    <group
                                        name={`sticeArcWall`}
                                        position={[0, -1.25 / 2 - 2, 0]}
                                    >
                                        {props.site.isLayoutPreview3d ? (
                                            <mesh
                                                visible={false}
                                                onClick={handleSelected}
                                                position={[0, 0.5, 0]}
                                            >
                                                <boxGeometry
                                                    args={[
                                                        Math.max(layoutBin.width, 2),
                                                        2.5,
                                                        Math.max(layoutBin.length, 2),
                                                    ]}
                                                />
                                                <meshBasicMaterial
                                                    transparent
                                                    opacity={0}
                                                    depthWrite={false}
                                                />
                                            </mesh>
                                        ) : null}
                                        <SticeArcWall
                                            layoutBin={layoutBin}
                                            layoutSides={props.site.layoutSides.filter(x => x.customerSiteLayoutBinId === layoutBin.customerSiteLayoutBinId)}
                                            binId={layoutBin.customerSiteLayoutBinId}
                                            binName={layoutBin.binName}
                                            groupId={layoutBin.customerSiteLayoutGroupId}
                                            height={1.25}
                                            binWidth={layoutBin.width}
                                            binLength={layoutBin.length}
                                            site={props.site}
                                        />
                                    </group>
                                )}
                                {layoutCablesForLayoutBin(props.site, layoutBin).map(layoutCable => (
                                    <CableNew
                                        key={`sticeBin_cable_${layoutCable.customerSiteLayoutCableId}`}
                                        layoutCable={layoutCable}
                                        layoutSensors={props.site.layoutSensors.filter(x => x.customerSiteLayoutCableId === layoutCable.customerSiteLayoutCableId)}
                                        layoutBin={layoutBin}
                                        site={props.site}
                                        radiansToNorth={cableRadiansInsideFlatProductionGroup(props.site, layoutBin)}
                                    />
                                ))}
                                <FanBin3d
                                    layoutBin={layoutBin}
                                    site={props.site}
                                    insideRotatedGroup
                                />
                                </group>
                            </group>
                    ))}
                </group>
            ))}
        </group>
    );
}

export default Stice;